import React, { useState, useEffect } from 'react';
import { useTodoContext } from '../context/TodoContext';
import { format } from 'date-fns';
import { useCategoryContext } from '../context/CategoryContext';
import Select from 'react-select';
import ShareTaskModal from './sharing/ShareTaskModal';

const TodoItem = ({ todo, getCategoryName, getCategoryColor }) => {
  const { updateTodo, deleteTodo } = useTodoContext();
  const { categories } = useCategoryContext();
  const [isEditing, setIsEditing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [updatedTodo, setUpdatedTodo] = useState({
    text: todo.text,
    priority: todo.priority,
    dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 16) : '',
    categories: todo.categories || [],
    completed: todo.completed
  });
  // Convert categories to react-select format
  const categoryOptions = categories.map(category => ({
    value: category._id,
    label: category.name,
    color: category.color
  }));

  // Custom styles for react-select
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: getContrastYIQ(state.data.color),
      ':active': {
        backgroundColor: state.data.color,
        opacity: 0.8
      },
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color,
      color: getContrastYIQ(state.data.color),
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      color: getContrastYIQ(state.data.color),
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: getContrastYIQ(state.data.color),
      ':hover': {
        backgroundColor: state.data.color,
        opacity: 0.8,
        color: getContrastYIQ(state.data.color),
      },
    }),
    control: (provided, state) => ({
      ...provided, 
      backgroundColor: 'var(--bg-input, white)',
      borderColor: 'var(--border-input, #e2e8f0)',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: 'var(--border-input-hover, #cbd5e1)'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--bg-dropdown, white)',
    }),
  };

  // Helper function to determine text color based on background color
  function getContrastYIQ(hexcolor) {
    // Default to white if no color
    if (!hexcolor || hexcolor === 'undefined') return '#ffffff';

    // Remove # if present
    hexcolor = hexcolor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    
    // Calculate YIQ to determine brightness
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    
    // Return black or white depending on brightness
    return (yiq >= 128) ? '#000000' : '#ffffff';
  }
  // Initialize selected categories when editing
  useEffect(() => {
    if (isEditing && todo.categories) {
      // Convert todo categories to react-select format
      const initialSelectedCategories = todo.categories.map(cat => {
        const catId = typeof cat === 'string' ? cat : cat._id;
        const category = categories.find(c => c._id === catId);
        return category ? {
          value: category._id,
          label: category.name,
          color: category.color
        } : null;
      }).filter(cat => cat !== null);
      
      setSelectedCategories(initialSelectedCategories);
    }
  }, [isEditing, todo.categories, categories]);

  // Console log to debug categories
  console.log('Todo item categories:', {
    todoId: todo._id,
    text: todo.text,
    todoCategories: todo.categories,
    updatedCategories: updatedTodo.categories,
    selectedCategories
  });

  const handleToggleComplete = async () => {
    try {
      await updateTodo(todo._id, {
        ...todo,
        completed: !todo.completed
      });
    } catch (err) {
      console.error('Error updating todo:', err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Sei sicuro di voler eliminare questa attività?')) {
      try {
        await deleteTodo(todo._id);
      } catch (err) {
        console.error('Errore durante l\'eliminazione dell\'attività:', err);
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setUpdatedTodo({
      text: todo.text,
      priority: todo.priority,
      dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 16) : '',
      categories: Array.isArray(todo.categories) ? 
        todo.categories.map(cat => typeof cat === 'string' ? cat : cat._id) : [],
      completed: todo.completed
    });
  };
  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      // Create a new object without modifying original todo
      const updatedData = {
        ...todo,
        text: updatedTodo.text,
        priority: updatedTodo.priority,
        dueDate: updatedTodo.dueDate || null,
        categories: updatedTodo.categories,
        completed: updatedTodo.completed
      };
      
      console.log("Updating todo with data:", updatedData);
      await updateTodo(todo._id, updatedData);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating todo:', err);
    }
  };

  const handleCancel = () => {
    setUpdatedTodo({
      text: todo.text,
      priority: todo.priority,
      dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 16) : '',
      categories: Array.isArray(todo.categories) ? 
        todo.categories.map(cat => typeof cat === 'string' ? cat : cat._id) : [],
      completed: todo.completed
    });
    setIsEditing(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUpdatedTodo({
      ...updatedTodo,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCategoryChange = (selectedOptions) => {
    setSelectedCategories(selectedOptions || []);
    // Extract category IDs for form data
    const categoryIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setUpdatedTodo({
      ...updatedTodo,
      categories: categoryIds
    });
  };
  
  const priorityLabels = {
    'high': 'Alta',
    'medium': 'Media',
    'low': 'Bassa'
  };
  
  // Format due date for display
  const formatDueDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Invalid date:', dateString);
      return '';
    }
  };
  
  // Determine if task is overdue
  const isOverdue = () => {
    if (!todo.dueDate || todo.completed) return false;
    const now = new Date();
    const dueDate = new Date(todo.dueDate);
    return dueDate < now;
  };

  // Priority color
  const priorityColor = () => {
    switch (todo.priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    }
  };
  if (isEditing) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-md shadow-md p-4">
        <form onSubmit={handleUpdate}>
          <div className="mb-3">
            <label htmlFor="text" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Attività
            </label>
            <input
              type="text"
              id="text"
              name="text"
              value={updatedTodo.text}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <label htmlFor="priority" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priorità
              </label>
              <select
                id="priority"
                name="priority"
                value={updatedTodo.priority}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Bassa</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
              </select>
            </div>

            <div>
              <label htmlFor="editCategories" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Categorie
              </label>
              <Select
                id="editCategories"
                name="categories"
                isMulti
                options={categoryOptions}
                className="react-select-container"
                classNamePrefix="react-select"
                value={selectedCategories}
                onChange={handleCategoryChange}
                placeholder="Seleziona categorie..."
                styles={customStyles}
              />
            </div>
            
            <div>
              <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Data di Scadenza
              </label>
              <input
                type="datetime-local"
                id="dueDate"
                name="dueDate"
                value={updatedTodo.dueDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-md text-gray-700 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="
      bg-white dark:bg-gray-800 rounded-md shadow-md p-4 transition-all">
      <div className="flex items-start relative">
        <div className="flex-shrink-0 mr-3 mt-1">
          <input
            type="checkbox"
            checked={todo.completed}
            onChange={handleToggleComplete}
            className="h-5 w-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
          />
        </div>
        
        <div className="flex-grow">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${priorityColor()}`}>
              {priorityLabels[todo.priority]}
            </span>
            
            {isOverdue() && (
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                Scaduta
              </span>
            )}
            
            {todo.dueDate && (
              <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                Scadenza: {formatDueDate(todo.dueDate)}
              </span>
            )}
          </div>
          
          <p className={`text-lg font-medium text-gray-800 dark:text-gray-200 ${todo.completed ? 'line-through text-gray-500 dark:text-gray-400' : ''}`}>
            {todo.text}
          </p>
          
          {Array.isArray(todo.categories) && todo.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {todo.categories.map(categoryId => {
                const catId = typeof categoryId === 'string' ? categoryId : categoryId._id;
                const categoryName = getCategoryName(catId);
                const categoryColor = getCategoryColor(catId);
                
                return categoryName ? (
                  <span
                    key={catId}
                    className="text-xs px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: categoryColor,
                      color: getContrastYIQ(categoryColor)
                    }}
                  >
                    {categoryName}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>
        <div className="flex-shrink-0 ml-3 space-x-1">
          <div className="flex space-x-1">
            <button
              onClick={() => setShowShareModal(true)}
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
              title="Condividi"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
            <button
              onClick={handleEdit}
              className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
              title="Modifica"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
              title="Elimina"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* ShareTaskModal component viene renderizzato al di fuori del flusso normale */}
        <ShareTaskModal
          task={todo}
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
    </div>
  );
};

export default TodoItem;
